import { useState } from 'react';
import { BanIcon } from 'lucide-react';
import { INSURER_GROUPS, type Provider } from '@kennisbank/shared';
import { apiPatch } from '../api/client.js';

/**
 * Zelf-bewerkbare aanmeldstop-strook — POH self-service. Zet/wist de
 * patiëntenstop op een praktijk, met optionele reden, optionele einddatum en
 * optioneel een verzekeraar-specifieke stop (geen labels = geldt voor iedereen).
 * Bewust één component voor zowel de rasterkaart (SocialeKaart) als de
 * kaart-popup (ProviderMap): de twee mogen niet uit elkaar lopen, anders kun je
 * vanaf de kaart iets anders zetten dan vanaf het raster.
 *
 * Houdt eigen lokale state los van de prop zodat de strook na een geslaagde
 * PATCH meteen klopt zonder een refetch af te wachten.
 */
const DATE_FMT = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  // Kale datum wordt als UTC-middernacht geparsed; formatteer ook in UTC zodat de
  // getoonde dag niet kan verschuiven t.o.v. de opgeslagen kalenderdag.
  timeZone: 'UTC',
});

function formatUntil(until: string): string {
  return DATE_FMT.format(new Date(until));
}

export function IntakeControl({ provider }: { provider: Provider }): JSX.Element {
  const [stopped, setStopped] = useState(provider.intakeStopped ?? false);
  const [reason, setReason] = useState(provider.intakeStoppedReason ?? '');
  const [until, setUntil] = useState(provider.intakeStoppedUntil ?? '');
  const [insurers, setInsurers] = useState<string[]>(provider.intakeStoppedInsurers ?? []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftInsurers, setDraftInsurers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(
    next: boolean,
    nextReason: string,
    nextUntil: string,
    nextInsurers: string[],
  ): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await apiPatch(`/api/v1/providers/${provider.id}/intake-status`, {
        stopped: next,
        ...(next && nextReason.trim() ? { reason: nextReason.trim() } : {}),
        ...(next && nextUntil ? { until: nextUntil } : {}),
        ...(next && nextInsurers.length > 0 ? { insurers: nextInsurers } : {}),
      });
      setStopped(next);
      setReason(next ? nextReason.trim() : '');
      setUntil(next ? nextUntil : '');
      setInsurers(next ? nextInsurers : []);
      setEditing(false);
    } catch {
      setError('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  }

  function toggleInsurer(label: string): void {
    setDraftInsurers((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label],
    );
  }

  function toggleConcern(labels: readonly string[], allOn: boolean): void {
    setDraftInsurers((prev) =>
      allOn ? prev.filter((x) => !labels.includes(x)) : [...new Set([...prev, ...labels])],
    );
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1.5 text-[11.5px]">
        <div className="flex flex-wrap items-center gap-1.5">
          <BanIcon size={12} aria-hidden="true" className="text-red-600" />
          <input
            type="text"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="reden (optioneel), bv. plafond bereikt"
            className="min-w-0 flex-1 rounded-md border border-brand-200 px-1.5 py-0.5 text-[11.5px] focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-200"
          />
          <input
            type="date"
            value={draftUntil}
            onChange={(e) => setDraftUntil(e.target.value)}
            title="Tot wanneer geldt de stop? (optioneel)"
            className="rounded-md border border-brand-200 px-1.5 py-0.5 text-[11.5px] focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-200"
          />
        </div>
        <details className="rounded-md border border-brand-100 bg-brand-50/40 px-2 py-1">
          <summary className="cursor-pointer select-none text-ink-900/70">
            Alleen bepaalde verzekeraars?{' '}
            {draftInsurers.length > 0 && (
              <span className="font-semibold text-red-700">({draftInsurers.length})</span>
            )}
          </summary>
          <p className="mt-1 text-ink-900/55">
            Niets aanvinken = stop geldt voor iedereen. Aanvinken = stop geldt alléén voor die
            verzekeraars.
          </p>
          <div className="mt-1.5 max-h-40 overflow-y-auto pr-1">
            {INSURER_GROUPS.map((g) => {
              const allOn = g.labels.every((l) => draftInsurers.includes(l));
              return (
                <div key={g.concern} className="mb-1.5">
                  <label className="flex items-center gap-1.5 font-semibold text-ink-900/75">
                    <input
                      type="checkbox"
                      checked={allOn}
                      onChange={() => toggleConcern(g.labels, allOn)}
                    />
                    {g.concern}
                  </label>
                  <div className="ml-4 flex flex-wrap gap-x-3 gap-y-0.5">
                    {g.labels.map((l) => (
                      <label key={l} className="flex items-center gap-1 text-ink-900/70">
                        <input
                          type="checkbox"
                          checked={draftInsurers.includes(l)}
                          onChange={() => toggleInsurer(l)}
                        />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={saving}
            onClick={() => void save(true, draft, draftUntil, draftInsurers)}
            className="rounded-md bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? 'Opslaan…' : 'Melden'}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded-md px-1.5 py-0.5 text-ink-900/55 hover:text-ink-900"
          >
            Annuleer
          </button>
          {error && <span className="text-red-600">{error}</span>}
        </div>
      </div>
    );
  }

  if (stopped) {
    const partial = insurers.length > 0;
    return (
      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
            partial ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'
          }`}
        >
          <BanIcon size={11} /> Aanmeldstop
        </span>
        {partial && <span className="text-ink-900/70">voor {insurers.join(', ')}</span>}
        {until && <span className="text-ink-900/60">tot {formatUntil(until)}</span>}
        {reason && <span className="text-ink-900/60">{reason}</span>}
        <button
          type="button"
          disabled={saving}
          onClick={() => void save(false, '', '', [])}
          className="text-brand-700/70 underline decoration-dotted hover:text-brand-700 disabled:opacity-50"
        >
          {saving ? 'Bezig…' : 'opheffen'}
        </button>
        {error && <span className="text-red-600">{error}</span>}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft('');
        setDraftUntil('');
        setDraftInsurers([]);
        setEditing(true);
      }}
      className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-900/50 hover:text-red-700"
      title="Meld dat deze praktijk tijdelijk geen nieuwe cliënten aanneemt"
    >
      <BanIcon size={12} aria-hidden="true" className="text-ink-900/30" />
      <span className="underline decoration-dotted">Aanmeldstop melden</span>
    </button>
  );
}
